/** Deterministic, native-pixel ink candidates. No OCR, model or image generation.
 * Local paper estimate -> relative contrast -> seeded connected components.
 * Rectangular separable morphology keeps the browser implementation O(pixels).
 * It identifies dark marks, not semantic handwriting: drawings/ruling may remain.
 */
export interface InkMaskResult {
  width: number;
  height: number;
  mask: Uint8Array;
  confidence: Uint8Array;
  inkRatio: number;
}

const MAX_PIXELS = 12_000_000;

/** Sliding max/min in each axis, with truncated windows at image boundaries. */
function extrema(input: Float32Array, width: number, height: number, radius: number, max: boolean): Float32Array {
  const tmp = new Float32Array(input.length);
  const out = new Float32Array(input.length);
  const queue = new Int32Array(Math.max(width, height));
  for (let axis = 0; axis < 2; axis++) {
    const src = axis === 0 ? input : tmp;
    const dst = axis === 0 ? tmp : out;
    const length = axis === 0 ? width : height;
    const lines = axis === 0 ? height : width;
    const step = axis === 0 ? 1 : width;
    for (let line = 0; line < lines; line++) {
      const base = axis === 0 ? line * width : line;
      let head = 0, tail = 0, next = 0;
      for (let i = 0; i < length; i++) {
        const end = Math.min(length - 1, i + radius);
        while (next <= end) {
          const value = src[base + next * step];
          while (tail > head && (max ? src[base + queue[tail - 1] * step] <= value : src[base + queue[tail - 1] * step] >= value)) tail--;
          queue[tail++] = next++;
        }
        while (queue[head] < i - radius) head++;
        dst[base + i * step] = src[base + queue[head] * step];
      }
    }
  }
  return out;
}

/** Mild fixed smoothing reduces JPEG noise without thresholding away pencil. */
function smooth(input: Float32Array, width: number, height: number): Float32Array {
  const tmp = new Float32Array(input.length), out = new Float32Array(input.length);
  for (let y = 0; y < height; y++) for (let x = 0; x < width; x++) {
    const i = y * width + x;
    tmp[i] = (input[y * width + Math.max(0, x - 1)] + 2 * input[i] + input[y * width + Math.min(width - 1, x + 1)]) / 4;
  }
  for (let y = 0; y < height; y++) for (let x = 0; x < width; x++) {
    const i = y * width + x;
    out[i] = (tmp[Math.max(0, y - 1) * width + x] + 2 * tmp[i] + tmp[Math.min(height - 1, y + 1) * width + x]) / 4;
  }
  return out;
}

export function detectInkMask(luminance: Uint8Array, width: number, height: number): InkMaskResult {
  const count = width * height;
  if (!Number.isSafeInteger(width) || !Number.isSafeInteger(height) || width <= 0 || height <= 0 || count > MAX_PIXELS || luminance.length !== count) {
    throw new Error('Invalid image dimensions/size (maximum 12 megapixels); select a smaller source image.');
  }
  const radius = Math.max(3, Math.min(18, Math.round(Math.min(width, height) / 180)));
  const gray = smooth(Float32Array.from(luminance), width, height);
  const paper = smooth(smooth(extrema(extrema(gray, width, height, radius, true), width, height, radius, false), width, height), width, height);
  const depth = new Float32Array(count);
  for (let i = 0; i < count; i++) depth[i] = Math.max(0, paper[i] - gray[i]);
  const peak = extrema(depth, width, height, Math.max(2, Math.round(radius / 2)), true);
  const candidate = new Uint8Array(count);
  const confidence = new Uint8Array(count);
  for (let i = 0; i < count; i++) {
    const low = Math.max(4.5, peak[i] * .34);
    if (depth[i] >= low) {
      candidate[i] = depth[i] >= Math.max(9, peak[i] * .65) ? 2 : 1;
      confidence[i] = Math.round(255 * Math.min(1, (depth[i] - low + 1.5) / Math.max(2, peak[i] * .24)));
    }
  }
  const mask = new Uint8Array(count);
  const queue = new Int32Array(count);
  let inkPixels = 0;
  for (let start = 0; start < count; start++) {
    if (!candidate[start]) continue;
    let head = 0, tail = 1, seeds = candidate[start] === 2 ? 1 : 0;
    let sides = 0;
    queue[0] = start; candidate[start] = 0;
    while (head < tail) {
      const i = queue[head++], x = i % width, y = Math.floor(i / width);
      if (x === 0) sides |= 1;
      if (x === width - 1) sides |= 2;
      if (y === 0) sides |= 4;
      if (y === height - 1) sides |= 8;
      for (let dy = -1; dy <= 1; dy++) for (let dx = -1; dx <= 1; dx++) {
        const nx = x + dx, ny = y + dy;
        if (nx < 0 || ny < 0 || nx >= width || ny >= height) continue;
        const next = ny * width + nx;
        if (!candidate[next]) continue;
        if (candidate[next] === 2) seeds++;
        queue[tail++] = next; candidate[next] = 0;
      }
    }
    // Frame-like components spanning opposite sides plus a third edge are rejected.
    // A clipped stroke touching one ROI boundary must retain its entire interior.
    // Keep small punctuation: no text-line or minimum letter-height filter.
    const frame = ((sides & 3) === 3 && (sides & 12) !== 0) || ((sides & 12) === 12 && (sides & 3) !== 0);
    if (!frame && tail >= 3 && seeds >= 2) {
      for (let i = 0; i < tail; i++) mask[queue[i]] = 1;
      inkPixels += tail;
    } else {
      for (let i = 0; i < tail; i++) confidence[queue[i]] = 0;
    }
  }
  return { width, height, mask, confidence, inkRatio: inkPixels / count };
}

/** Paint a swept disk in source coordinates; fast pointer samples leave no gaps. */
export function paintMaskStroke(mask: Uint8Array, width: number, height: number, from: {x: number; y: number}, to: {x: number; y: number}, radius: number, keep: boolean): void {
  if (![from.x, from.y, to.x, to.y, radius].every(Number.isFinite) || radius < 0) return;
  const dx = to.x - from.x, dy = to.y - from.y;
  const lengthSq = dx * dx + dy * dy;
  const left = Math.max(0, Math.floor(Math.min(from.x, to.x) - radius));
  const right = Math.min(width - 1, Math.ceil(Math.max(from.x, to.x) + radius));
  const top = Math.max(0, Math.floor(Math.min(from.y, to.y) - radius));
  const bottom = Math.min(height - 1, Math.ceil(Math.max(from.y, to.y) + radius));
  for (let y = top; y <= bottom; y++) for (let x = left; x <= right; x++) {
    const t = lengthSq ? Math.max(0, Math.min(1, ((x - from.x) * dx + (y - from.y) * dy) / lengthSq)) : 0;
    if ((x - from.x - t * dx) ** 2 + (y - from.y - t * dy) ** 2 <= radius * radius) mask[y * width + x] = keep ? 1 : 0;
  }
}

/** Highlight only selected pixels: no dilation, halo or modification of source. */
export function maskOverlay(mask: Uint8Array, confidence?: Uint8Array): Uint8ClampedArray {
  const rgba = new Uint8ClampedArray(mask.length * 4);
  for (let i = 0; i < mask.length; i++) {
    if (!mask[i]) continue;
    rgba[i * 4] = 255; rgba[i * 4 + 1] = 225; rgba[i * 4 + 2] = 15;
    rgba[i * 4 + 3] = Math.round(.62 * (confidence ? confidence[i] : 255));
  }
  return rgba;
}

/** Place an analyzed crop on the original pixel grid; outside stays unselected. */
export function placeRegionMask(region: InkMaskResult, width: number, height: number, x: number, y: number): InkMaskResult {
  if (![width,height,x,y].every(Number.isSafeInteger) || width<=0 || height<=0 || width*height>MAX_PIXELS || x<0 || y<0 || x+region.width>width || y+region.height>height) throw new Error('Invalid region dimensions.');
  const mask=new Uint8Array(width*height),confidence=new Uint8Array(width*height);
  for(let row=0;row<region.height;row++) {
    const from=row*region.width,to=(row+y)*width+x;
    mask.set(region.mask.subarray(from,from+region.width),to);
    confidence.set(region.confidence.subarray(from,from+region.width),to);
  }
  return {width,height,mask,confidence,inkRatio:region.inkRatio*region.width*region.height/(width*height)};
}
